/// ビジネスデータとしての HTTP(S) URL (branded 型 + 構文バリデーション)。
///
/// 対象フィールド: `firms.website_url` / `lawyers.personal_site_url` /
/// `contracts.contract_document_url` など。
///
/// バリデーション規則は backend `Url` VO (`domain/src/core/value_objects.rs`) と揃える:
///   - 空文字 / whitespace / 制御文字を含む → null (パーサが修正してしまうため)
///   - バックスラッシュを含む → null (WHATWG パーサが `/` に書き換えてしまうため)
///   - `http://` / `https://` スキームのみ許可
///   - `URL` でパース不可 → null
///   - 大文字スキーム (`HTTP://`) → null (スキームは小文字必須)
///   - 入力文字列をそのまま保持 (正規化なし)

/** 構文検証済みの HTTP(S) URL。 */
export type Url = string & { readonly __brand: 'Url' };

/**
 * `value` を検証し、問題なければ `Url` 型で返す。
 *
 * 無効な場合は `null` を返す (例外は投げない)。
 *
 * - whitespace / 制御文字を含む場合は無効。
 * - バックスラッシュを含む場合は無効。
 * - `http://` または `https://` で始まらない場合は無効。
 * - `URL` クラスでパースできない場合は無効。
 */
export function createUrl(value: string): Url | null {
  // whitespace / 制御文字チェック
  // Backend: `value.chars().any(|c| c.is_whitespace() || c.is_control())`
  // JS の `\s` は U+FEFF を含み Rust の `is_whitespace()` より広いため `\p{White_Space}` を使う。
  for (const ch of value) {
    const code = ch.codePointAt(0) ?? 0;
    if (/\p{White_Space}/u.test(ch) || code <= 0x1f || code === 0x7f || (code >= 0x80 && code <= 0x9f)) {
      return null;
    }
  }

  // バックスラッシュチェック
  // Backend: `if value.contains('\\') { return Err(...) }`
  if (value.includes('\\')) {
    return null;
  }

  // パースを試みてスキームを確認
  // Backend: `url::Url::parse` → scheme が http/https か確認
  let parsed: URL;
  try {
    parsed = new URL(value);
  } catch {
    return null;
  }

  // スキームは http / https のみ
  // Backend: `!matches!(parsed.scheme(), "http" | "https")`
  if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
    return null;
  }

  // 明示的な小文字スキーム `scheme://` で始まることを確認
  // Backend: `let expected_prefix = format!("{}://", parsed.scheme()); if !value.starts_with(&expected_prefix)`
  // これにより大文字スキーム (`HTTP://`) と authority-less 形式 (`http:example.com`) を弾く
  const expectedPrefix = `${parsed.protocol.slice(0, -1)}://`;
  if (!value.startsWith(expectedPrefix)) {
    return null;
  }

  return value as Url;
}
